import { useEffect, useMemo } from 'react';
import { useStore } from 'zustand';

import type { MenuItem } from 'app/components/commons/MenuCustom/MenuCustom';
import { ExtranetHeader } from 'app/components/pages/Extranet/commons/ExtranetHeader/ExtranetHeader';

import { PackType, packagesStore } from '../../PackageStore';
import { useFilteredPackages } from '../../_hooks/useFilterPackages';

import styles from './PackagesHeader.module.scss';
import { PackagesAction } from './_components/PackagesAction';
import { ScrollableMenu } from './_components/ScrollableMenu';

type PackMenuItem = MenuItem<string> & { id: number };

export type PackagesHeaderProps = {
  page: string;
};

export const PackagesHeader = ({ page }: PackagesHeaderProps) => {
  const { filteredNightStayPacks, filteredDaytimePacks } =
    useFilteredPackages();
  const packType = useStore(packagesStore, (state) => state.packType);
  const selectedPackId = useStore(
    packagesStore,
    (state) => state.selectedPackId
  );
  const setSelectedPackId = useStore(
    packagesStore,
    (state) => state.setSelectedPackId
  );

  const menuItems: PackMenuItem[] = useMemo(
    () =>
      (packType === PackType.NIGHT_STAY
        ? filteredNightStayPacks
        : filteredDaytimePacks
      ).map((pack) => ({ id: pack.id, name: pack.nameForHotel })),
    [packType, filteredNightStayPacks, filteredDaytimePacks]
  );

  useEffect(() => {
    const first = menuItems[0];

    if (!first) {
      return;
    }

    const isCurrentPackValid = menuItems.some(
      (item) => item.id === selectedPackId
    );

    if (!isCurrentPackValid) {
      setSelectedPackId(first.id);
    }
  }, [menuItems, selectedPackId, setSelectedPackId]);

  const selectedMenuItem: PackMenuItem = useMemo(
    () =>
      menuItems.find((item) => item.id === selectedPackId) ??
      menuItems[0] ?? { id: 0, name: '' },
    [menuItems, selectedPackId]
  );

  return (
    <div className={styles.extranetPackagesHeader}>
      <ExtranetHeader page={page} className="no-border-bottom" />
      <div className={styles.packagesHeaderContent}>
        <div className={styles.actionsPart}>
          <PackagesAction hasPreviewButton={menuItems.length > 0} />
        </div>
        {menuItems.length > 0 && (
          <div className={styles.packagesMenu}>
            <ScrollableMenu
              menuItems={menuItems}
              selectedMenu={selectedMenuItem}
              setSelectedMenu={(menuItem) =>
                setSelectedPackId((menuItem as PackMenuItem).id)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
